import React from 'react';
import { Info } from 'lucide-react';
import { cn } from '@/lib/utils';
import { colors } from '../../theme/colors';
import { Tooltip, type TooltipPlacement, type TooltipVariant } from '../tool-tip';

export type LabelSize = 'small' | 'medium' | 'large';
export type LabelWeight = 400 | 500 | 600 | 700;
export interface LabelProps {
  children: React.ReactNode;
  htmlFor?: string;
  required?: boolean;
  size?: LabelSize;
  weight?: LabelWeight;
  disabled?: boolean;
  className?: string;
  /** Tooltip content - can be string or React node */
  tooltip?: React.ReactNode;
  /** Tooltip placement */
  tooltipPlacement?: TooltipPlacement;
  /** Tooltip variant */
  tooltipVariant?: TooltipVariant;
  /** Custom tooltip icon */
  tooltipIcon?: React.ReactNode;
  /** Tooltip icon size */
  tooltipIconSize?: number;
}

const sizeClasses: Record<LabelSize, string> = {
  small: 'text-xs mb-1',
  medium: 'text-sm mb-1.5',
  large: 'text-base mb-2',
};

const weightClasses: Record<LabelWeight, string> = {
  400: 'font-normal',
  500: 'font-medium',
  600: 'font-semibold',
  700: 'font-bold',
};

// Icon size based on label size
const iconSizeMap: Record<LabelSize, number> = {
  small: 12,
  medium: 14,
  large: 16,
};

export const Label: React.FC<LabelProps> = ({
  children,
  htmlFor,
  required = false,
  size = 'medium',
  weight = 500,
  disabled = false,
  className,
  tooltip,
  tooltipPlacement = 'top',
  tooltipVariant = 'default',
  tooltipIcon,
  tooltipIconSize,
}) => {
  const iconSize = tooltipIconSize ?? iconSizeMap[size];

  return (
    <label
      htmlFor={htmlFor}
      className={cn(
        'inline-flex items-center gap-1',
        sizeClasses[size],
        weightClasses[weight],
        className
      )}
      style={{ color: disabled ? colors.textMuted : colors.textDefault }}
    >
      <span>{children}</span>
      {required && (
        <span style={{ color: colors.error }}>
          *
        </span>
      )}
      {tooltip && (
        <Tooltip
          title={tooltip}
          placement={tooltipPlacement}
          variant={tooltipVariant}
        >
          <span
            className="inline-flex items-center cursor-help"
            style={{ color: colors.textMuted }}
          >
            {tooltipIcon ?? <Info size={iconSize} />}
          </span>
        </Tooltip>
      )}
    </label>
  );
};

export default Label;
